import { useState, useEffect, useCallback, useRef } from 'react';
import { getChecklistInstance, updateChecklistItem } from './checklistApi';

export function useChecklistInstance(instanceId) {
    const [instance, setInstance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const updateQueueRef = useRef([]);
    const isUpdatingRef = useRef(false);
    const inputDebounceTimeoutRef = useRef({});

    useEffect(() => {
        const fetchInstance = async () => {
            try {
                const instanceData = await getChecklistInstance(instanceId);
                const augmentedInstanceData = {
                    ...instanceData,
                    groups: instanceData.groups.map((group, groupIndex) => ({
                        ...group,
                        _mongoId: group._id,
                        _id: group._id || `group-${groupIndex}`,
                        items: group.items.map((item, itemIndex) => ({
                            ...item,
                            _mongoId: item._id,
                            _id: item._id || `group-${groupIndex}-item-${itemIndex}`
                        }))
                    }))
                };
                setInstance(augmentedInstanceData);
            } catch (err) {
                setError('Failed to fetch checklist instance.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (instanceId) {
            fetchInstance();
        }
    }, [instanceId]);

    // Queue-based update system to prevent concurrent updates and race conditions
    const processUpdateQueue = useCallback(async () => {
        if (isUpdatingRef.current || updateQueueRef.current.length === 0) {
            return;
        }

        isUpdatingRef.current = true;
        const update = updateQueueRef.current.shift();

        try {
            await updateChecklistItem(
                instanceId,
                update.groupId,
                update.itemId,
                update.data
            );
        } catch (err) {
            console.error('Failed to update checklist item:', err);
            setError('Failed to save changes.');
        } finally {
            isUpdatingRef.current = false;
            // Process next update if any
            if (updateQueueRef.current.length > 0) {
                processUpdateQueue();
            }
        }
    }, [instanceId]);

    const queueUpdate = useCallback((groupId, itemId, data) => {
        updateQueueRef.current.push({ groupId, itemId, data });
        processUpdateQueue();
    }, [processUpdateQueue]);

    const handleCheckChange = useCallback((groupId, itemId) => {
        setInstance(prevInstance => {
            const targetGroup = prevInstance.groups.find(g => g._id === groupId);
            if (!targetGroup) return prevInstance;

            const targetItem = targetGroup.items.find(i => i._id === itemId);
            if (!targetItem) return prevInstance;

            const mongoGroupId = targetGroup._mongoId;
            const mongoItemId = targetItem._mongoId;
            const newIsChecked = !targetItem.isChecked;

            // Update the state
            const newGroups = prevInstance.groups.map(group => {
                if (group._id !== groupId) {
                    return group;
                }

                const newItems = group.items.map(item => {
                    if (item._id !== itemId) {
                        return item;
                    }

                    const updatedItem = { ...item, isChecked: newIsChecked };
                    if (updatedItem.isChecked) {
                        updatedItem.lastChanged = new Date();
                    }

                    return updatedItem;
                });
                return { ...group, items: newItems };
            });

            // Save immediately (no debounce for checkboxes)
            if (mongoGroupId && mongoItemId) {
                queueUpdate(mongoGroupId, mongoItemId, { isChecked: newIsChecked });
            }

            return { ...prevInstance, groups: newGroups };
        });
    }, [queueUpdate]);

    const handleOptionalFieldChange = useCallback((itemId, updatedValues) => {
        setInstance(prevInstance => {
            // Find the group and item to get their MongoDB IDs
            let mongoGroupId = null;
            let mongoItemId = null;

            for (const group of prevInstance.groups) {
                const targetItem = group.items.find(i => i._id === itemId);
                if (targetItem) {
                    mongoGroupId = group._mongoId;
                    mongoItemId = targetItem._mongoId;
                    break;
                }
            }

            // Update the state
            const newGroups = prevInstance.groups.map(group => ({
                ...group,
                items: group.items.map(item => {
                    if (item._id === itemId) {
                        return {
                            ...item,
                            optionalFieldValues: updatedValues,
                        };
                    }
                    return item;
                }),
            }));

            // Set up 4-second debounce for auto-save
            if (mongoGroupId && mongoItemId) {
                const debounceKey = `${itemId}-input`;

                // Clear existing timeout for this field
                if (inputDebounceTimeoutRef.current[debounceKey]) {
                    clearTimeout(inputDebounceTimeoutRef.current[debounceKey]);
                }

                // Set new timeout for 4-second auto-save
                inputDebounceTimeoutRef.current[debounceKey] = setTimeout(() => {
                    queueUpdate(mongoGroupId, mongoItemId, { optionalFieldValues: updatedValues });
                    delete inputDebounceTimeoutRef.current[debounceKey];
                }, 4000);
            }

            return { ...prevInstance, groups: newGroups };
        });
    }, [queueUpdate]);

    const saveOptionalField = useCallback((itemId) => {
        // Clear any pending debounce and save immediately
        if (itemId) {
            const debounceKey = `${itemId}-input`;
            if (inputDebounceTimeoutRef.current[debounceKey]) {
                clearTimeout(inputDebounceTimeoutRef.current[debounceKey]);
                delete inputDebounceTimeoutRef.current[debounceKey];
            }
        } else {
            // Clear all debounce timeouts if no specific itemId
            Object.keys(inputDebounceTimeoutRef.current).forEach(key => {
                clearTimeout(inputDebounceTimeoutRef.current[key]);
            });
            inputDebounceTimeoutRef.current = {};
        }

        // Find the item and queue the update
        if (instance && itemId) {
            let mongoGroupId = null;
            let mongoItemId = null;
            let optionalFieldValues = null;

            for (const group of instance.groups) {
                const item = group.items.find(i => i._id === itemId);
                if (item) {
                    mongoGroupId = group._mongoId;
                    mongoItemId = item._mongoId;
                    optionalFieldValues = item.optionalFieldValues;
                    break;
                }
            }

            if (mongoGroupId && mongoItemId && optionalFieldValues) {
                queueUpdate(mongoGroupId, mongoItemId, { optionalFieldValues });
            }
        }
    }, [instance, queueUpdate]);

    const saveInstance = useCallback(async () => {
        if (!instance) return;

        // Clear all debounce timers and save immediately
        Object.keys(inputDebounceTimeoutRef.current).forEach(key => {
            clearTimeout(inputDebounceTimeoutRef.current[key]);
        });
        inputDebounceTimeoutRef.current = {};

        // Wait for all pending updates to complete
        while (updateQueueRef.current.length > 0 || isUpdatingRef.current) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }, [instance]);

    return {
        instance,
        loading,
        error,
        handleCheckChange,
        handleOptionalFieldChange,
        saveOptionalField,
        saveInstance,
    };
}
